import { Component, OnInit, OnDestroy } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { interval, Subject } from "rxjs";
import { takeUntil, take } from "rxjs/operators";

interface ApiResponse {
  success: boolean;
  message?: string;
  data?: any;
}

@Component({
  selector: "email-otp",
  templateUrl: "./email-otp.component.html",
  styleUrls: ["./email-otp.component.css"],
})
export class EmailOtpComponent implements OnInit, OnDestroy {
  loginForm: FormGroup;
  otpForm: FormGroup;

  // Step management
  currentStep: string = "uniqueId"; // 'uniqueId' | 'email' | 'otp' | 'success'

  // Loading states
  isCheckingUniqueId: boolean = false;
  isSendingOTP: boolean = false;
  isVerifyingOTP: boolean = false;

  // OTP timer
  otpTimer: number = 300;
  timerSubscription: any;
  otpExpired: boolean = false;

  // Messages
  errorMessage: string = "";
  successMessage: string = "";

  // Student data
  studentData: any = null;

  private destroy$ = new Subject<void>();
  private apiUrl: string = "https://issouae.ae"; // Update with your API URL

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
  ) {
    this.loginForm = this.fb.group({
      uniqueId: ["", [Validators.required, Validators.minLength(6)]],
      email: ["", [Validators.required, Validators.email]],
    });

    this.otpForm = this.fb.group({
      otp: ["", [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  ngOnInit() {
    // Component is freely accessible - no auth check
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  // Step 1: Verify Unique ID
  verifyUniqueId() {
    const uniqueIdControl = this.loginForm.get("uniqueId");
    if (uniqueIdControl && uniqueIdControl.invalid) {
      this.errorMessage = "Please enter a valid unique ID";
      return;
    }

    this.isCheckingUniqueId = true;
    this.errorMessage = "";

    const uniqueId = this.loginForm.get("uniqueId").value.trim();

    this.http
      .post<ApiResponse>(
        this.apiUrl +
          "/isso-php/staffadmin/studentProfile/VerifyStudentId/verify_unique_id",
        {
          uniqueId: uniqueId,
        },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: ApiResponse) => {
          this.isCheckingUniqueId = false;

          if (response.success) {
            this.studentData = response.data;
            this.currentStep = "email";
            this.successMessage =
              "Unique ID verified! Please enter your email.";
          } else {
            this.errorMessage = response.message || "Invalid Unique ID";
          }
        },
        (error) => {
          this.isCheckingUniqueId = false;
          this.errorMessage = "Error verifying Unique ID. Please try again.";
          console.error("Error:", error);
        },
      );
  }

  // Step 2: Send OTP
  sendOTP() {
    const emailControl = this.loginForm.get("email");
    if (emailControl && emailControl.invalid) {
      this.errorMessage = "Please enter a valid email address";
      return;
    }

    this.isSendingOTP = true;
    this.errorMessage = "";
    this.successMessage = "";

    const email = this.loginForm.get("email").value.trim();
    const uniqueId = this.loginForm.get("uniqueId").value.trim();

    this.http
      .post<ApiResponse>(
        this.apiUrl +
          "/isso-php/staffadmin/studentProfile/VerifyStudentId/send_otp",
        {
          email: email,
          uniqueId: uniqueId,
        },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: ApiResponse) => {
          this.isSendingOTP = false;

          if (response.success) {
            this.currentStep = "otp";
            this.successMessage =
              "OTP sent successfully! Please check your email.";
            this.startOTPTimer();
          } else {
            this.errorMessage =
              response.message || "Email not found in our records";
          }
        },
        (error) => {
          this.isSendingOTP = false;
          this.errorMessage = "Error sending OTP. Please try again.";
          console.error("Error:", error);
        },
      );
  }

  // Step 3: Verify OTP
  verifyOTP() {
    if (this.otpForm.invalid) {
      this.errorMessage = "Please enter a valid 6-digit OTP";
      return;
    }

    if (this.otpExpired) {
      this.errorMessage = "OTP has expired. Please request a new one.";
      return;
    }

    this.isVerifyingOTP = true;
    this.errorMessage = "";

    const otp = this.otpForm.get("otp").value;
    const email = this.loginForm.get("email").value;
    const uniqueId = this.loginForm.get("uniqueId").value;

    this.http
      .post<ApiResponse>(
        this.apiUrl +
          "/isso-php/staffadmin/studentProfile/VerifyStudentId/verify_otp",
        {
          email: email,
          uniqueId: uniqueId,
          otp: otp,
        },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: ApiResponse) => {
          this.isVerifyingOTP = false;

          if (response.success) {
            this.currentStep = "success";
            this.successMessage = "Login successful! Redirecting...";
            console.log("ILoginm data--->", JSON.stringify(response.data));
            // Store user session data
            localStorage.setItem("studentData", JSON.stringify(response.data));
            localStorage.setItem("isLoggedIn", "true");

            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
              this.router.navigate(["parent-dashboard"]);
              // window.location.href = "http://localhost:4200/#/parent-dashboard";
            }, 2000);
          } else {
            this.errorMessage = response.message || "Invalid OTP";
          }
        },
        (error) => {
          this.isVerifyingOTP = false;
          this.errorMessage = "Error verifying OTP. Please try again.";
          console.error("Error:", error);
        },
      );
  }

  // Resend OTP
  resendOTP() {
    this.otpExpired = false;
    this.otpForm.reset();
    this.sendOTP();
  }

  // OTP Timer
  startOTPTimer() {
    this.otpTimer = 300;
    this.otpExpired = false;

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }

    this.timerSubscription = interval(1000)
      .pipe(take(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.otpTimer--;
        if (this.otpTimer === 0) {
          this.otpExpired = true;
          this.errorMessage = "OTP has expired. Please request a new one.";
        }
      });
  }

  // Reset form
  resetForm() {
    this.loginForm.reset();
    this.otpForm.reset();
    this.currentStep = "uniqueId";
    this.errorMessage = "";
    this.successMessage = "";
    this.otpExpired = false;
    this.otpTimer = 300;
    this.studentData = null;

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  // Helper methods for template
  get uniqueIdControl() {
    return this.loginForm.get("uniqueId");
  }

  get emailControl() {
    return this.loginForm.get("email");
  }

  get otpControl() {
    return this.otpForm.get("otp");
  }

  isUniqueIdStep(): boolean {
    return this.currentStep === "uniqueId";
  }

  isEmailStep(): boolean {
    return this.currentStep === "email";
  }

  isOtpStep(): boolean {
    return this.currentStep === "otp";
  }

  isSuccessStep(): boolean {
    return this.currentStep === "success";
  }

  isStepActive(step: string): boolean {
    return this.currentStep === step;
  }

  isStepCompleted(step: string): boolean {
    const steps = ["uniqueId", "email", "otp", "success"];
    const currentIndex = steps.indexOf(this.currentStep);
    const stepIndex = steps.indexOf(step);
    return currentIndex > stepIndex;
  }

  goToEmailStep() {
    this.currentStep = "email";
  }

  goToUniqueIdStep() {
    this.currentStep = "uniqueId";
  }

  hasUniqueIdError(): boolean {
    const control = this.uniqueIdControl;
    return control && control.invalid && control.touched;
  }

  hasEmailError(): boolean {
    const control = this.emailControl;
    return control && control.invalid && control.touched;
  }

  hasOtpError(): boolean {
    const control = this.otpControl;
    return control && control.invalid && control.touched;
  }

  hasRequiredError(controlName: string): boolean {
    const control =
      this.loginForm.get(controlName) || this.otpForm.get(controlName);
    return control && control.hasError("required");
  }

  hasMinLengthError(controlName: string): boolean {
    const control = this.loginForm.get(controlName);
    return control && control.hasError("minlength");
  }

  hasEmailFormatError(): boolean {
    const control = this.emailControl;
    return control && control.hasError("email");
  }

  hasPatternError(controlName: string): boolean {
    const control = this.otpForm.get(controlName);
    return control && control.hasError("pattern");
  }
}
